import type { FC } from "react";

import { useDispatch, useSelector } from "react-redux";

import { showSuccessToast } from "app/actions/toast_actions";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import type { ThunkDispatchCommon } from "single-spa/store/types";

import trackingService from "../../../../../../sub-modules/advanced-search/services/tracking/advancedSearchTrackingService";
import { deleteSavedSearchModalToggledAction } from "../../../../../../sub-modules/advanced-search/store/action-creators";
import { deleteSavedSearchThunk } from "../../../../../../sub-modules/advanced-search/store/effects";
import {
    selectCurrentSavedSearchObject,
    selectSavedSearchDeleteModalIsOpened,
    selectSavedSearchToBeDeleted,
    selectSearchIsDeleting,
    selectSearchDeleteError,
} from "../../../../../../sub-modules/advanced-search/store/selectors";

import SavedSearchDeleteModal from "../../components/SavedSearchDeleteModal/SavedSearchDeleteModal";

type SavedSearchToDelete = ReturnType<typeof selectSavedSearchToBeDeleted>;

type SavedSearchDeleteModalContainerProps = {
    onDelete?: () => void;
};

const SavedSearchDeleteModalContainer: FC<SavedSearchDeleteModalContainerProps> = ({ onDelete }) => {
    const translate = useTranslation();
    const dispatch: ThunkDispatchCommon = useDispatch();
    const isModalOpened: boolean = useSelector(selectSavedSearchDeleteModalIsOpened);
    const isDeleteInProgress: boolean = useSelector(selectSearchIsDeleting);
    const searchDeleteError: string | undefined = useSelector(selectSearchDeleteError);
    const savedSearchToBeDeleted: SavedSearchToDelete = useSelector(selectSavedSearchToBeDeleted);
    const currentSavedSearchObject: ReturnType<typeof selectCurrentSavedSearchObject> =
        useSelector(selectCurrentSavedSearchObject);

    useActionAfterFlagChange(
        isDeleteInProgress,
        () => {
            dispatch(showSuccessToast(translate("si.advanced_search.saved_search.delete_success_toast")));

            if (savedSearchToBeDeleted?.searchId === currentSavedSearchObject?.searchId) {
                proNavigator.go("salesIntelligence-advancedSearch-newSearch");
            }
        },
        typeof searchDeleteError === "undefined",
    );

    const handleDeleteConfirmed = async () => {
        const existingSavedSearch = savedSearchToBeDeleted as NonNullable<SavedSearchToDelete>;

        trackingService.quickSearches.trackSavedSearchDeleted(existingSavedSearch.name);
        await dispatch(deleteSavedSearchThunk(existingSavedSearch.searchId));

        onDelete?.();
    };

    const handleDeleteCanceled = () => {
        dispatch(deleteSavedSearchModalToggledAction(null));
    };

    return (
        <SavedSearchDeleteModal
            savedSearch={savedSearchToBeDeleted}
            isOpened={isModalOpened}
            isBeingDeleted={isDeleteInProgress}
            onCancel={handleDeleteCanceled}
            onConfirm={handleDeleteConfirmed}
        />
    );
};

export default SavedSearchDeleteModalContainer;
